import React, { useCallback, useMemo } from 'react';
import { Dropdown, Menu } from 'antd';
import { useNavigate } from 'react-router-dom';
import Button from '../../../../core/components/button/Button';
import { useAppDispatch, useAppSelector } from '../../../../store/hooks';
import { stockBulkCountersSelector } from '../../../../store/selectors/stockBulkSelector';
import { stockPartRequestsLinesViewFiltersSelector } from '../../../../store/selectors/stockPartRequestsSelector';
import { createStockBulkOperation } from '../../../../store/thunks/stock/stock-part-requests/stockBulkOperationsThunks';
import { routesPath } from '../../../../core/enums/pathEnum';
import { BulkRequestTypeEnum, getBulkRequestTypeLabel } from '../../../stock-bulk/BulkRequestTypeEnum';

export const StockPartRequestBulkSelect = () => {
  const dispatch = useAppDispatch();
  const counters = useAppSelector(stockBulkCountersSelector);
  const navigate = useNavigate();
  const filters = useAppSelector(stockPartRequestsLinesViewFiltersSelector);

  const handleBulkOperation = useCallback(async (operation: BulkRequestTypeEnum) => {
    const result = await dispatch(createStockBulkOperation({ operation, filter: filters }));
    if (result.meta.requestStatus === 'fulfilled') {
      const search = new URLSearchParams({ operation });
      navigate({
        pathname: `/${routesPath.STOCK}/${routesPath.STOCK_BULK}`,
        search: search.toString(),
      }, {
        replace: true,
      });
    }
  }, [filters, dispatch, navigate]);

  const menu = useMemo(() => (
    <Menu>
      <Menu.Item
        key={BulkRequestTypeEnum.ReturnToStock}
        disabled={!counters.bulkReturnToStockCount}
        onClick={() => handleBulkOperation(BulkRequestTypeEnum.ReturnToStock)}
      >
        {getBulkRequestTypeLabel(BulkRequestTypeEnum.ReturnToStock)}
        {' '}
        (
        {counters.bulkReturnToStockCount}
        )
      </Menu.Item>
      <Menu.Item
        key={BulkRequestTypeEnum.ReviewPricelistSource}
        disabled={!counters.bulkReviewPricelistSourceCount}
        onClick={() => handleBulkOperation(BulkRequestTypeEnum.ReviewPricelistSource)}
      >
        {getBulkRequestTypeLabel(BulkRequestTypeEnum.ReviewPricelistSource)}
        {' '}
        (
        {counters.bulkReviewPricelistSourceCount}
        )
      </Menu.Item>
      <Menu.Item
        key={BulkRequestTypeEnum.ReviewStockSource}
        disabled={!counters.bulkReviewStockSourceCount}
        onClick={() => handleBulkOperation(BulkRequestTypeEnum.ReviewStockSource)}
      >
        {getBulkRequestTypeLabel(BulkRequestTypeEnum.ReviewStockSource)}
        {' '}
        (
        {counters.bulkReviewStockSourceCount}
        )
      </Menu.Item>
      <Menu.Item
        key={BulkRequestTypeEnum.MarkAsReady}
        disabled={!counters.bulkMarkAsReadyCount}
        onClick={() => handleBulkOperation(BulkRequestTypeEnum.MarkAsReady)}
      >
        {getBulkRequestTypeLabel(BulkRequestTypeEnum.MarkAsReady)}
        {' '}
        (
        {counters.bulkMarkAsReadyCount}
        )
      </Menu.Item>
      <Menu.Item
        key={BulkRequestTypeEnum.MarkAsCollected}
        disabled={!counters.bulkMarkAsCollectedCount}
        onClick={() => handleBulkOperation(BulkRequestTypeEnum.MarkAsCollected)}
      >
        {getBulkRequestTypeLabel(BulkRequestTypeEnum.MarkAsCollected)}
        {' '}
        (
        {counters.bulkMarkAsCollectedCount}
        )
      </Menu.Item>
      <Menu.Item
        key={BulkRequestTypeEnum.GeneratePurchaseOrder}
        disabled={!counters.bulkCreatePurchaseOrderCount}
        onClick={() => handleBulkOperation(BulkRequestTypeEnum.GeneratePurchaseOrder)}
      >
        {getBulkRequestTypeLabel(BulkRequestTypeEnum.GeneratePurchaseOrder)}
        {' '}
        (
        {counters.bulkCreatePurchaseOrderCount}
        )
      </Menu.Item>
    </Menu>
  ), [counters, handleBulkOperation]);

  return (
    <Dropdown overlay={menu} trigger={['click']}>
      <Button
        label="Bulk actions"
      />
    </Dropdown>
  );
};
